import { Component, type ErrorInfo, type ReactNode } from 'react';
import { translate } from '@/i18n/labels';
import { useLangStore } from '@/stores/langStore';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      // Class component — no hooks. Read the current UI language directly.
      const lang = useLangStore.getState().lang;

      return (
        <div
          role="alert"
          style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#dc2626',
          }}
        >
          <h2>{translate('errorBoundaryTitle', lang)}</h2>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            {translate('errorBoundaryFallbackMessage', lang)}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {translate('errorBoundaryTryAgain', lang)}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
